// CommHub Push Notifications via Firebase Cloud Messaging
import { initializeApp, FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import axios from 'axios';

const COMMHUB_BASE = 'https://www.commhub.cloud/api';
const API_KEY = 'GlHLVQpgdQaYcNoXxMoUKFTcUwXgZME_4RUZ6Z0AztA';
const APP_ID = '3d6ef532-9aa9-467c-817b-627582e0972e';

let firebaseApp: FirebaseApp | null = null;
let messaging: Messaging | null = null;
let firebaseConfig: Record<string, string> | null = null;

// Fetch Firebase config from CommHub
async function getFirebaseConfig(): Promise<Record<string, string> | null> {
  if (firebaseConfig) return firebaseConfig;
  
  try {
    const response = await axios.get(`${COMMHUB_BASE}/push/web-config`, {
      headers: { 'X-API-Key': API_KEY }
    });
    
    if (response.data?.configured && response.data?.config) {
      firebaseConfig = response.data.config;
      return firebaseConfig;
    }
    return null;
  } catch (error) {
    console.error('Failed to fetch Firebase config:', error);
    return null;
  }
}

// Initialize Firebase
async function initFirebase(): Promise<boolean> {
  if (firebaseApp && messaging) return true;
  
  const config = await getFirebaseConfig();
  if (!config) {
    console.error('Firebase config not available');
    return false;
  }
  
  try {
    firebaseApp = initializeApp(config);
    messaging = getMessaging(firebaseApp);
    return true;
  } catch (error) {
    console.error('Failed to initialize Firebase:', error);
    return false;
  }
}

// Check if push is supported
export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// Check if already subscribed (has token stored)
export async function isSubscribed(): Promise<boolean> {
  if (!isPushSupported()) return false;
  
  const token = localStorage.getItem('fcm_token');
  return !!token;
}

// Request notification permission
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    return 'denied';
  }
  
  if (Notification.permission === 'granted') {
    return 'granted';
  }
  
  if (Notification.permission === 'denied') {
    return 'denied';
  }
  
  return await Notification.requestPermission();
}

// Subscribe to push notifications
export async function subscribeToPush(): Promise<string> {
  if (!isPushSupported()) {
    throw new Error('Push notifications are not supported');
  }

  // Request permission first
  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission denied');
  }

  // Initialize Firebase
  const initialized = await initFirebase();
  if (!initialized || !messaging) {
    throw new Error('Failed to initialize Firebase');
  }

  try {
    // Register service worker for Firebase
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    
    // Get FCM token
    const config = await getFirebaseConfig();
    const token = await getToken(messaging, {
      vapidKey: config?.vapidKey || undefined,
      serviceWorkerRegistration: registration
    });

    if (!token) {
      throw new Error('Failed to get FCM token');
    }

    // Store token locally
    localStorage.setItem('fcm_token', token);

    // Register token with CommHub
    const userId = localStorage.getItem('user_id') || 'anonymous';
    await registerTokenWithCommHub(token, userId);

    // Set up message listener
    onMessage(messaging, (payload) => {
      console.log('Message received:', payload);
      if (payload.notification) {
        showLocalNotification(
          payload.notification.title || 'Ny notifikation',
          payload.notification.body || ''
        );
      }
    });

    return token;
  } catch (error) {
    console.error('Failed to subscribe to push:', error);
    throw error;
  }
}

// Register token with CommHub
async function registerTokenWithCommHub(token: string, userId: string): Promise<void> {
  try {
    await axios.post(
      `${COMMHUB_BASE}/push/tokens`,
      {
        app_id: APP_ID,
        user_id: userId,
        device_token: token,
        platform: 'web'
      },
      {
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json'
        }
      }
    );
    console.log('Token registered with CommHub');
  } catch (error) {
    console.error('Failed to register token with CommHub:', error);
    // Don't throw - token is still valid locally
  }
}

// Unsubscribe from push
export async function unsubscribeFromPush(): Promise<void> {
  const token = localStorage.getItem('fcm_token');
  localStorage.removeItem('fcm_token');
  
  if (token) {
    // Could optionally unregister from CommHub here
    console.log('Unsubscribed from push notifications');
  }
}

// Show a local notification
export function showLocalNotification(title: string, body: string): void {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }
  
  new Notification(title, {
    body,
    icon: '/logo192.png'
  });
}

// Subscribe to a topic
export async function subscribeToTopic(topic: string): Promise<void> {
  const token = localStorage.getItem('fcm_token');
  if (!token) {
    throw new Error('No FCM token - subscribe to push first');
  }

  try {
    await axios.post(
      `${COMMHUB_BASE}/push/topics/subscribe`,
      { token, topic },
      {
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json'
        }
      }
    );
    console.log(`Subscribed to topic: ${topic}`);
  } catch (error) {
    console.error('Failed to subscribe to topic:', error);
    throw error;
  }
}

// Unsubscribe from a topic
export async function unsubscribeFromTopic(topic: string): Promise<void> {
  const token = localStorage.getItem('fcm_token');
  if (!token) return;

  try {
    await axios.post(
      `${COMMHUB_BASE}/push/topics/unsubscribe`,
      { token, topic },
      {
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json'
        }
      }
    );
    console.log(`Unsubscribed from topic: ${topic}`);
  } catch (error) {
    console.error('Failed to unsubscribe from topic:', error);
  }
}
